import { useState } from 'react';
import { Button, StyleSheet, Text, useTheme, View } from '@/components/common';
import { useTranslation } from '@/lib/i18n';
import type { FormData as FormDataType, FormField } from '@/types';
import FormSchemaRenderer from '@/components/forms/FormSchemaRenderer';
import { embeddedFormDesign as design } from './embeddedDesign';

interface EmbeddedFormProps {
  formData: FormDataType;
  onSuccess?: () => void;
}

function toStringValue(value: unknown): string {
  if (value === null || value === undefined) return '';
  return typeof value === 'string' ? value : String(value);
}

function parseJsonMaybe(value: unknown): any | undefined {
  if (value === null || value === undefined) return undefined;
  if (typeof value !== 'string') return value;
  const trimmed = value.trim();
  if (!trimmed) return undefined;
  try {
    return JSON.parse(trimmed);
  } catch {
    return undefined;
  }
}

function buildInitialValues(fields: FormField[]): Record<string, any> {
  return fields.reduce<Record<string, any>>((acc, field) => {
    if (field.type === 'checkbox') {
      acc[field.name] = Boolean(field.defaultValue);
      return acc;
    }

    if (field.type === 'json') {
      const parsed = parseJsonMaybe(field.defaultValue);
      acc[field.name] = parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : {};
      return acc;
    }

    acc[field.name] = toStringValue(field.defaultValue);
    return acc;
  }, {});
}

type ValidationMessages = {
  required: string;
  invalidFormat: string;
  invalidEmail: string;
  invalidPhone: string;
};

function validateField(field: FormField, value: unknown, messages: ValidationMessages): string | null {
  if (field.type === 'json') {
    const obj = value && typeof value === 'object' && !Array.isArray(value) ? (value as Record<string, any>) : null;
    const hasAnyValue = obj ? Object.values(obj).some((v) => String(v ?? '').trim().length > 0) : false;
    if (field.required && !hasAnyValue) return messages.required;
    return null;
  }

  const trimmed = toStringValue(value).trim();

  if (field.required && !trimmed) return messages.required;
  if (!trimmed) return null;

  if (field.validation?.pattern) {
    try {
      const re = new RegExp(field.validation.pattern);
      if (!re.test(trimmed)) return messages.invalidFormat;
    } catch {
      // Ignore invalid regex patterns.
    }
  }

  if (field.type === 'email') {
    // Keep lightweight; backend can do stricter validation.
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(trimmed)) return messages.invalidEmail;
  }

  if (field.type === 'tel') {
    // AU-centric but permissive; aligns with existing intent.
    if (!/^(\+?61|0)[2-478](?:[ -]?[0-9]){8}$/.test(trimmed)) return messages.invalidPhone;
  }

  return null;
}

export function EmbeddedForm({ formData, onSuccess }: EmbeddedFormProps) {
  const theme = useTheme();
  const { t } = useTranslation();

  const [values, setValues] = useState<Record<string, any>>(() => buildInitialValues(formData.fields));
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const submit = async () => {
    const validationMessages = {
      required: t('forms.validation.required', 'This field is required.'),
      invalidFormat: t('forms.validation.invalidFormat', 'Invalid format.'),
      invalidEmail: t('forms.validation.invalidEmail', 'Invalid email address.'),
      invalidPhone: t('forms.validation.invalidPhone', 'Invalid phone number.'),
    };
    const nextErrors: Record<string, string> = {};
    for (const field of formData.fields) {
      const raw = values[field.name];
      const message = validateField(field, field.type === 'checkbox' ? (raw ? 'true' : '') : raw, validationMessages);
      if (message) nextErrors[field.name] = message;
    }

    setErrors(nextErrors);
    setSubmitError(null);
    if (Object.keys(nextErrors).length > 0) return;

    setIsSubmitting(true);
    try {
      await formData.onSubmit({ ...values });
      setValues(buildInitialValues(formData.fields));
      setErrors({});
      setSubmitError(null);
      onSuccess?.();
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Form submission error:', error);
      setSubmitError(t('forms.submitError', 'We could not submit this form. Check the details and try again.'));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <View style={[styles.container, { backgroundColor: theme.colors.surface }]}> 
      <View style={styles.header}>
        <Text variant="titleMedium" style={styles.title}>{formData.title}</Text>
        {formData.description ? (
          <Text
            variant="bodyMedium"
            style={[styles.description, { color: theme.colors.onSurfaceVariant }]}
          >
            {formData.description}
          </Text>
        ) : null}
      </View>

      <View style={styles.fields}>
        <FormSchemaRenderer
          schema={{ fields: formData.fields as any }}
          values={values}
          onChangeValues={(next) => setValues(next)}
          disabled={isSubmitting}
          errors={errors}
          onChangeField={(fieldKey) => {
            if (!errors[fieldKey]) return;
            setErrors((prev) => {
              const next = { ...prev };
              delete next[fieldKey];
              return next;
            });
          }}
          testIDPrefix="embedded-form"
        />
      </View>

      {submitError ? (
        <Text
          variant="bodySmall"
          accessibilityRole="alert"
          style={[styles.submitError, { color: theme.colors.error, borderColor: theme.colors.error }]}
        >
          {submitError}
        </Text>
      ) : null}

      <Button
        mode="contained"
        onPress={() => {
          void submit();
        }}
        loading={isSubmitting}
        disabled={isSubmitting}
      >
        {formData.submitLabel || t('common.submit', 'Submit')}
      </Button>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    borderRadius: design.containerRadius,
    padding: design.containerPadding,
    gap: design.containerGap,
    borderWidth: 1,
    borderColor: design.borderColor,
  },
  header: {
    gap: design.headerGap,
  },
  title: {
    fontSize: design.titleFontSize,
    lineHeight: design.titleLineHeight,
  },
  description: {
    fontSize: design.descriptionFontSize,
    lineHeight: design.descriptionLineHeight,
  },
  fields: {
    gap: design.fieldsGap,
  },
  submitError: {
    borderWidth: 1,
    borderRadius: design.submitErrorRadius,
    paddingHorizontal: design.submitErrorPaddingHorizontal,
    paddingVertical: design.submitErrorPaddingVertical,
    fontSize: design.submitErrorFontSize,
    lineHeight: design.submitErrorLineHeight,
  },
});
